import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Input,
  Table,
  Space,
  Tooltip,
  message,
  Popconfirm,
} from "antd";
import dayjs from "dayjs";
import {
  PlusOutlined,
  FileExcelOutlined,
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import axiosInstance, { baseURL } from "../../../configs";
import { getUser } from "../../../utils/DataUser";
import { canEdit, canDelete, MODULES } from "../../../utils/rbac";

const { Title, Text } = Typography;

const defaultPageSize = 20;

export default function BrowseItem() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: defaultPageSize,
    total: 0,
  });

  // filter
  const [searchName, setSearchName] = useState("");
  const [searchCode, setSearchCode] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const fetchItems = async (page = 1, pageSize = defaultPageSize) => {
    setLoading(true);
    try {
      const user = getUser();

      const params = {
        page,
        limit: pageSize,
        customer_name: searchName || undefined,
        document_number: searchCode || undefined,
      };

      if (user && (user.id_user_level === 7 || user.id_user_level === "7")) {
        params.id_customer = user.username;
      }

      const res = await axiosInstance.get("/outgoing", { params });

      const raw = res.data || {};
      const payload = raw.data || raw;
      const rows =
        payload.items ||
        payload.rows ||
        payload.data ||
        (Array.isArray(payload) ? payload : []);

      const total = raw.pagination?.total ||
        payload.total ||
        payload.count ||
        payload.pagination?.total ||
        rows.length;

      setItems(rows);
      setPagination({
        current: page,
        pageSize,
        total: total || 0,
      });
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat daftar pengeluaran barang");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(1, defaultPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchItems(1, pagination.pageSize);
  };

  const handleResetFilter = () => {
    setSearchName("");
    setSearchCode("");
    fetchItems(1, defaultPageSize);
  };

  const handleTableChange = (pager) => {
    fetchItems(pager.current, pager.pageSize);
  };

  const handleExportExcel = () => {
    window.open(`${baseURL}/outgoing/export`, "_blank");
  };

  const handleDelete = async (record) => {
    setDeletingId(record.id_outgoing);
    try {
      await axiosInstance.delete(`/outgoing/${record.id_outgoing}`);
      message.success("Pengeluaran barang berhasil dihapus");
      fetchItems(pagination.current, pagination.pageSize);
    } catch (err) {
      console.error(err);
      message.error("Gagal menghapus pengeluaran barang");
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (value) =>
    value ? dayjs(value).format("DD-MM-YY") : "-";

  const columns = [
    {
      title: "NO",
      dataIndex: "index",
      width: 50,
      align: "center",
      fixed: "left",
      render: (text, record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "NO SURAT JALAN",
      dataIndex: "delivery_number",
      width: 120,
      ellipsis: true,
    },
    {
      title: "TGL SJ",
      dataIndex: "delivery_date",
      width: 90,
      render: (value) => formatDate(value),
    },
    {
      title: "KODE DOK",
      dataIndex: "id_document",
      width: 80,
      responsive: ["md"],
      render: (value) => value || "-",
    },
    {
      title: "NO DOKUMEN",
      dataIndex: "document_number",
      width: 130,
      ellipsis: true,
      render: (value) => value || "-",
    },
    {
      title: "TGL DOK",
      dataIndex: "document_date",
      width: 90,
      responsive: ["lg"],
      render: (value) => formatDate(value),
    },
    {
      title: "PEMILIK",
      dataIndex: "nama_pemilik",
      width: 140,
      ellipsis: true,
      responsive: ["sm"],
      render: (value, record) =>
        value || record.id_customer ? value || record.id_customer : "-",
    },
    {
      title: "AKSI",
      key: "action",
      width: 90,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          {canEdit(MODULES.INVENTORY) && (
            <Tooltip title={getUser().id_user_level === 7 || getUser().id_user_level === "7" ? "Lihat" : "Edit"}>
              <Link to={`/inventory/updateoutgoing/${record.id_outgoing}`}>
                <Button
                  size="small"
                  type="primary"
                  icon={getUser().id_user_level === 7 || getUser().id_user_level === "7" ? <SearchOutlined /> : <EditOutlined />}
                  style={{
                    background: "#3699FF",
                    borderColor: "#3699FF",
                  }}
                />
              </Link>
            </Tooltip>
          )}

          {canDelete(MODULES.INVENTORY) && getUser().id_user_level !== 7 && getUser().id_user_level !== "7" && (
            <Popconfirm
              title="Hapus data ini?"
              okText="Ya"
              cancelText="Batal"
              okButtonProps={{
                danger: true,
                loading: deletingId === record.id_outgoing,
              }}
              onConfirm={() => handleDelete(record)}
            >
              <Tooltip title="Hapus">
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  loading={deletingId === record.id_outgoing}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card className="kt-portlet" bodyStyle={{ padding: "16px" }}>
      {/* HEADER TITLE + ACTIONS */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={14}>
          <Title level={4} style={{ marginBottom: 4 }}>
            Browse Pengeluaran Barang
          </Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Kelola daftar Pengeluaran Barang untuk inventory & kepabeanan.
          </Text>
        </Col>
        <Col xs={24} md={10} style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          {canEdit(MODULES.INVENTORY) && getUser().id_user_level !== 7 && getUser().id_user_level !== "7" && (
            <Link to="/inventory/addoutgoing">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{
                  background: "#1BC5BD",
                  borderColor: "#1BC5BD",
                  fontWeight: 600,
                  color: "#fff",
                }}
              >
                Tambah
              </Button>
            </Link>
          )}
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={handleExportExcel}
            style={{
              background: "#1E1EFA",
              borderColor: "#1E1EFA",
              fontWeight: 600,
            }}
          >
            Excel
          </Button>
        </Col>
      </Row>

      {/* FILTER SECTION */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={8}>
          <Text strong style={{ fontSize: 12 }}>Pemilik Barang</Text>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Cari..."
            style={{ marginTop: 4 }}
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Text strong style={{ fontSize: 12 }}>No Pendaftaran</Text>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Cari..."
            style={{ marginTop: 4 }}
            value={searchCode}
            onChange={(e) => setSearchCode(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Col>

        <Col xs={24} md={8} style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={handleResetFilter}>
              Reset
            </Button>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              style={{
                background: "#8950FC",
                borderColor: "#8950FC",
              }}
            >
              Cari
            </Button>
          </Space>
        </Col>
      </Row>

      {/* TABLE */}
      <Table
        style={{ marginTop: 16 }}
        rowKey="id_outgoing"
        dataSource={items}
        columns={columns}
        loading={loading}
        size="small"
        scroll={{ x: 700 }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          size: "small",
          showTotal: (total) => `Total ${total} data`,
        }}
        onChange={handleTableChange}
      />
    </Card>
  );
}
